<template>
  <div>
    <v-card class="elevation-2">
      <DataTableHeader
        :pagination="pagination"
        :searchAttributes="searchAttributes"
        :selectedSearchAttributes.sync="selectedSearchAttributes"
        :disabled="loading" />
      <DataTableSkeletonLoader :loading="initialLoad" />
      <v-data-table
        v-show="!initialLoad"
        :headers="headers"
        :items="entities"
        :dense="$vuetify.breakpoint.xs && !loading"
        :loading="loading"
        :loading-text="$t('components.noData.fetchingEntries')"
        :options.sync="options"
        :server-items-length="pagination.total"
        :items-per-page="pagination.per_page"
        :header-props="{
          sortIcon: 'arrow_circle_up',
          sortByText: $t('miscellaneous.sortBy'),
        }"
        :class="{ 'mobile-empty-datatable': $vuetify.breakpoint.xsOnly }"
        hide-default-footer>
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>{{
              $t('entities.sessions.title')
            }}</v-toolbar-title>
            <v-divider class="mx-4" inset vertical></v-divider>
            <small class="mr-2">{{
              addThousandSeparator(pagination.total)
            }}</small>
            <RefreshButton :refresh="refresh" :loading="loading" />
          </v-toolbar>
        </template>
        <template v-slot:item.id="{ item }">
          <a
            class="link primary--text"
            :title="item.id"
            @click.stop="openSession(item)">
            {{ short(item.id) }}
          </a>
        </template>
        <template v-slot:item.username="{ item }">
          {{ item.user.username }}
        </template>
        <template v-slot:item.device_id="{ item }">
          {{ item.device.hostname }}
        </template>
        <template v-slot:item.games="{ item }">
          <div
            v-for="(sessionGame, index) in item.games || []"
            :key="index"
            class="d-flex align-center my-1">
            <v-avatar size="28px" class="mr-2">
              <img
                v-if="
                  sessionGame.game &&
                  (sessionGame.game.icon || sessionGame.game.image)
                "
                :src="sessionGame.game.icon || sessionGame.game.image"
                alt="" />
              <v-icon v-else color="warning" small>mdi-alert</v-icon>
            </v-avatar>
            <span class="mr-2">
              {{
                sessionGame.game
                  ? sessionGame.game.name
                  : $t('entities.games.unknownGame')
              }}
            </span>
            <span class="grey--text text-no-wrap">
              {{ formatGameDuration(sessionGame.duration) }}
            </span>
          </div>
        </template>
        <template v-slot:item.arena_id="{ item }">
          <template v-if="item.arena_id">
            {{ item.arena_id }}
          </template>
        </template>
        <template v-slot:header.cost="{ header }">
          {{ header.text }}
          <CoinIcon />
        </template>
        <template v-slot:item.cost="{ item }">
          {{ item.cost != null ? addThousandSeparator(item.cost) : '' }}
          <CoinIcon v-if="item.cost != null" />
        </template>
        <template v-slot:item.started_at="{ item }">
          {{ fromUtcDateTimeToCurrent(item.started_at, true, true) }}
        </template>
        <template v-slot:item.stopped_at="{ item }">
          <template v-if="item.stopped_at">
            {{ fromUtcDateTimeToCurrent(item.stopped_at, true, true) }}
          </template>
          <template v-else>
            <v-tooltip color="transparent" top>
              <template v-slot:activator="{ on }">
                <v-btn
                  @click="stopSession(item)"
                  v-on="on"
                  :disabled="
                    !$store.getters.arena ||
                    loading ||
                    loadingIndexes.includes(item.id)
                  "
                  color="error"
                  class="glow ml-2"
                  depressed
                  x-small
                  fab>
                  <template v-if="loadingIndexes.includes(item.id)">
                    <ProgressCircular color="error" />
                  </template>
                  <template v-else>
                    <v-icon class="white--text" small>
                      power_settings_new
                    </v-icon>
                  </template>
                </v-btn>
              </template>
              <v-card class="elevation-2 px-4 py-2">
                <span>{{ $t('entities.sessions.stop') }}</span>
              </v-card>
            </v-tooltip>
          </template>
        </template>
        <template v-slot:item.duration="{ item }">
          {{ formatSessionDuration(item) }}
        </template>
        <template v-slot:no-data>
          <NoData
            :pagination="pagination"
            :typing="typing"
            :fetchEntities="fetchEntities" />
        </template>
      </v-data-table>
      <DataTableFooter :pagination="pagination" :loading="loading" />
    </v-card>

    <CoinSessionDialog
      :value="sessionDialogOpen"
      :session="sessionToView"
      @input="sessionDialogOpen = $event" />
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'
import CoinIcon from '@/components/CoinIcon.vue'
import NoData from '@/components/crud/NoData.vue'
import ProgressCircular from '@/components/ProgressCircular.vue'
import RefreshButton from '@/components/crud/RefreshButton.vue'
import DataTableHeader from '@/components/crud/DataTableHeader.vue'
import DataTableSkeletonLoader from '@/components/crud/DataTableSkeletonLoader.vue'
import DataTableFooter from '@/components/crud/DataTableFooter.vue'
import Session from '@/models/main/Session'
import CoinSessionDialog from '@/views/users/CoinSessionDialog.vue'

import { axiosCatch } from '@/mixins/crud/methods/axios-catch.js'
import { paginationWatch } from '@/mixins/crud/watch/pagination.js'
import thousandSeparator from '@/mixins/thousand-separator'
import convertDate from '@/mixins/convert-date'
import isRole from '@/mixins/crud/computed/isRole'
import { EventBus } from '@/eventBus'

export default Vue.extend({
  components: {
    RefreshButton,
    NoData,
    ProgressCircular,
    DataTableHeader,
    DataTableSkeletonLoader,
    DataTableFooter,
    CoinIcon,
    CoinSessionDialog,
  },

  mixins: [thousandSeparator, axiosCatch, paginationWatch, convertDate, isRole],

  data: () => ({
    initialLoad: true,
    loading: false,
    typing: false,
    sessionDialogOpen: false,
    sessionToView: null,
    loadingIndexes: [],
    entities: [],
    now: Date.now(),
    nowInterval: null,
    fetching: false,
    activeQuery: null,
    pagination: {
      search: '',
      current_page: 1,
      from: 1,
      last_page: 0,
      path: '',
      per_page: 10,
      to: 0,
      total: 0,
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['started_at'],
      sortDesc: ['DESC'],
      groupBy: [],
      groupDesc: [],
      mustSort: false,
      multiSort: false,
    },
    selectedSearchAttributes: ['Username'],
  }),

  computed: {
    headers() {
      const headers = [
        {
          text: this.$t('entities.invoices.attributes.id'),
          align: 'left',
          value: 'id',
          sortable: false,
        },
        {
          text: this.$t('entities.users.attributes.username'),
          align: 'left',
          value: 'username',
        },
        {
          text: this.$t('entities.devices.entity'),
          align: 'left',
          value: 'device_id',
        },
        {
          text: this.$t('entities.games.title'),
          align: 'left',
          value: 'games',
          sortable: false,
        },
        {
          text: this.$t('entities.sessions.attributes.cost'),
          align: 'left',
          value: 'cost',
          sortable: false,
        },
        {
          text: this.$t('entities.sessions.attributes.startedAt'),
          align: 'left',
          value: 'started_at',
        },
        {
          text: this.$t('entities.sessions.attributes.stoppedAt'),
          align: 'left',
          value: 'stopped_at',
        },
        {
          text: this.$t('entities.sessions.attributes.duration'),
          align: 'left',
          value: 'duration',
          sortable: false,
        },
      ]

      if (!this.$store.getters.arena) {
        headers.splice(0, 0, {
          text: this.$t('miscellaneous.arena'),
          align: 'left',
          value: 'arena.name',
          sortable: false,
        })
      }

      return headers
    },
    searchAttributes() {
      return [
        {
          label: this.$t('entities.users.attributes.username'),
          value: 'Username',
        },
        { label: this.$t('entities.devices.entity'), value: 'Device' },
      ]
    },
  },

  mounted() {
    this.refresh()
    this.nowInterval = setInterval(() => {
      this.now = Date.now()
    }, 1000)
  },

  created() {
    EventBus.$on('scanUserDialogClosed', this.refresh)
  },

  beforeDestroy() {
    clearInterval(this.interval)
    clearInterval(this.nowInterval)
    EventBus.$off('scanUserDialogClosed')
  },

  methods: {
    ...mapActions(['showSnackbar']),

    formatGameDuration(seconds) {
      if (seconds == null) return ''
      const total = Math.floor(Number(seconds))
      if (!Number.isFinite(total) || total < 0) return ''
      const hours = Math.floor(total / 3600)
      const mins = Math.floor((total % 3600) / 60)
      const secs = total % 60
      const parts = []
      if (hours) parts.push(`${hours}h`)
      if (mins) parts.push(`${mins}m`)
      if (secs) parts.push(`${secs}s`)
      return parts.join(' ') || '0s'
    },

    formatSessionDuration(item) {
      if (!item.started_at) return ''
      const start = new Date(item.started_at).getTime()
      const stop = item.stopped_at
        ? new Date(item.stopped_at).getTime()
        : this.now
      if (Number.isNaN(start) || Number.isNaN(stop)) return ''
      let total = Math.floor((stop - start) / 1000)
      if (total < 0) total = 0
      const hours = Math.floor(total / 3600)
      const mins = Math.floor((total % 3600) / 60)
      const secs = total % 60
      const parts = []
      if (hours) parts.push(`${hours}h`)
      if (mins) parts.push(`${mins}m`)
      if (secs) parts.push(`${secs}s`)
      return parts.join(' ') || '0s'
    },

    async fetchEntities() {
      let append = this.setParams()
      // Change param name
      if (append.includes('device=')) {
        append = append.replace('device=', 'hostname=')
      }
      // A single pagination change can trigger this more than once; skip the
      // duplicate request when an identical one is already in flight.
      if (this.fetching && this.activeQuery === append) {
        return
      }
      this.activeQuery = append
      this.fetching = true
      try {
        this.loading = true
        let response
        if (this.isAdmin) {
          if (this.$store.getters.arena) {
            response = await Session.api.getByArena(
              this.$store.getters.arena.id,
              `${append}`,
            )
          } else {
            response = await Session.api.get(`${append}`)
          }
        } else {
          response = await Session.api.getByArena(
            this.$store.getters.arena ? this.$store.getters.arena.id : -1,
            `${append}`,
          )
        }
        const data = response.data
        this.entities = data.data.map((SessionDTO) => new Session(SessionDTO))
        this.pagination.current_page = data.metadata.page
        this.pagination.per_page = data.metadata.size
        this.pagination.total = data.metadata.count
        this.pagination.last_page = Math.ceil(
          data.metadata.count / data.metadata.size,
        )
        this.pagination.from = (data.metadata.page - 1) * data.metadata.size + 1
        this.pagination.to =
          this.pagination.from + data.metadata.size - 1 > this.pagination.total
            ? this.pagination.total
            : this.pagination.from + data.metadata.size - 1
      } catch (e) {
        console.warn(`Sessions API failed.`)
        console.log(e)
      } finally {
        this.initialLoad = false
        this.loading = false
        this.fetching = false
      }
    },
    async stopSession(entity) {
      try {
        this.loadingIndexes.push(entity.id)
        this.loading = true
        const data = {
          reason: 'closed_by_employee',
        }
        await Session.api
          .destroy(this.$store.getters.arena.id, entity.id, data)
          .then(() => {
            this.refresh()
          })
          .catch((error) => {
            this.loading = false
            // Server error
            if (error.response.status === 500) {
              this.showSnackbar({
                color: 'error',
                message: error.response.data,
              })
            }
          })
      } catch (error) {
        console.warn(`Delete Arena Session API failed.`)
        console.log(error)
      } finally {
        this.loadingIndexes = this.loadingIndexes.filter((i) => i !== entity.id)
      }
    },
    refresh() {
      this.fetchEntities()
    },
    short(value) {
      if (!value) return ''
      return String(value).slice(0, 8)
    },
    openSession(item) {
      this.sessionToView = item
      this.sessionDialogOpen = true
    },
  },
})
</script>

<style scoped>
.link {
  cursor: pointer;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}
</style>
